import { faAngleRight, faAnglesRight, faArrowDown, faArrowRight, faArrowUp, faArrowUpRightFromSquare, faArrowsV, faBan, faBars, faBell, faCalendar, faChartLine, faCheck, faChevronCircleRight, faChevronDown, faChevronLeft, faChevronRight, faChevronUp, faCircle, faCircleMinus, faCog, faComment, faCopy, faDownload, faEdit, faEllipsisV, faExclamationTriangle, faEye, faEyeSlash, faFileUpload, faFolder, faGlobe, faHourglassEnd, faKey, faLink, faMinusCircle, faPlus, faQuestionCircle, faRobot, faRotateRight, faSave, faShieldHalved, faSignInAlt, faSignOutAlt, faSitemap, faSquare, faSyncAlt, faTableCells, faThLarge, faTimes, faTrash } from '@fortawesome/free-solid-svg-icons';

/************************************************************************************************************************************************
 * NAVIGATIONAL - Generic icons that take some action but do NOT modify data (e.g., change the UI, navigate to a new page, open a modal, etc.). *
 ***********************************************************************************************************************************************/

/** Navigate to a UI where something new can be created. */
export const ICON_ADD_CREATE = faPlus;

/** Close a page, window, modal, etc. */
export const ICON_CLOSE = faTimes;

/** Used in color picker use cases to allow the user to specify colors. Differs from ICON_COLOR_CIRCLE in that this supports user-selectable colors. */
export const ICON_COLOR_SQUARE = faSquare;

/** Create a comment, note, or discussion. */
export const ICON_COMMENT = faComment;

/** Copy something (e.g., populate a form with existing values). */
export const ICON_COPY = faCopy;

/** Download a file. */
export const ICON_DOWNLOAD = faDownload;

/** Navigate to a UI where something existing can be edited/modified/updated. */
export const ICON_EDIT_MODIFY_UPDATE = faEdit;

/** Expand or collapse an element of section of the page. */
export const ICON_EXPAND_COLLAPSE = faAngleRight;

/** Switch to a layout where content is displayed as a grid (probably of "cards"). */
export const ICON_LAYOUT_GRID = faThLarge;

/** Switch to a layout where content is displayed as a list (probably of rows in a table). */
export const ICON_LAYOUT_LIST = faBars;

/** Create/modify/remove a connection between two entities. */
export const ICON_LINK_OR_MAP = faLink;

/** Scroll down, expand something, or move something down. */
export const ICON_MOVE_OR_SCROLL_DOWN = faChevronDown;

/** Scroll left (e.g., a carousel). */
export const ICON_MOVE_OR_SCROLL_LEFT = faChevronLeft;

/** Scroll right (e.g., a carousel). */
export const ICON_MOVE_OR_SCROLL_RIGHT = faChevronRight;

/** Scroll up, collapse something, or move something up. */
export const ICON_MOVE_OR_SCROLL_UP = faChevronUp;

/** Open in a new browser tab. */
export const ICON_NEW_TAB = faArrowUpRightFromSquare;

/** Change the ordering of something, such as moving items up or down in a list. */
export const ICON_ORDERING = faArrowsV;

/** Open an overflow menu. */
export const ICON_OVERFLOW = faEllipsisV;

/** Refresh or reload something. Can also be used to indicate something is loading by spinning. */
export const ICON_REFRESH = faSyncAlt;

/** Navigate to a UI where settings/configurations for something can be updated. This is Similar to ICON_EDIT_MODIFY_UPDATE, but this is more related to changing settings (either globally or entity settings), whereas ICON_EDIT_MODIFY_UPDATE is more related to modifying an entity itself. */
export const ICON_SETTINGS = faCog;

/** Upload a file. */
export const ICON_UPLOAD = faFileUpload;

/*************************************************************************************************************
 * TRANSACTIONAL - Generic icons that take some action that DOES modify data (create, update, delete, etc.). *
 ************************************************************************************************************/

/** Delete or remove something. */
export const ICON_DELETE_REMOVE = faTrash;

/** Restart or reset something to its initial state. */
export const ICON_RESTART = faRotateRight;

/** Save changes to something existing, such as an entity, setting, or configuration. Also used to save a "draft" version of an entity. In a multi-step workflow, use this when saving changes that do NOT move the workflow forward. Use ICON_SUBMIT when saving changes that DO move the workflow forward. */
export const ICON_SAVE = faSave;

/** Submit a form to create something new, or push a workflow to the next step. * In a multi-step workflow, use this when saving changes that DO move the workflow forward. Use ICON_SAVE when saving changes that do NOT move the workflow forward. */
export const ICON_SUBMIT = faChevronCircleRight;

/**********************************************************************************************************************************************************************************************************************************************
 * STATIC / SPECIFIC - Icons that convey specific information. May be used in navigational contexts, like clicking a button that uses ICON_CALENDAR to pop a date selector or clicking a button that uses ICON_VIEW to view something in read-only mode. *
 *********************************************************************************************************************************************************************************************************************************************/

/** Indicate direction from one step to the next in a workflow. */
export const ICON_ARROW_RIGHT = faArrowRight;

/** The icon for Auto-Advance functionality. */
export const ICON_AUTO_ADVANCE = faAnglesRight;

/** Call attention to something, such as a warning or alert. */
export const ICON_ALERT_WARNING = faExclamationTriangle;

/** Indicate a "negative" trend. This is similar to ICON_NEGATIVE, but is more appropriate when showing a change over time. */
export const ICON_ARROW_DOWN = faArrowDown;

/** Indicate a "positive" scenario. Alternative to ICON_POSITIVE when an arrow is desired (e.g., showing a positive trend). */
export const ICON_ARROW_UP = faArrowUp;

/** Represent a date or calendar. */
export const ICON_CALENDAR = faCalendar;

/** Apply a range of colored circle icons to represent ratings (e.g., low-high, weak-strong, etc.). Differs from ICON_COLOR_SQUARE in that this is hardcoded. */
export const ICON_COLOR_CIRCLE = faCircle;

/** Indicates that a feature is not enabled/active. */
export const ICON_DISABLED_FEATURE = faCircleMinus;

/** Represent the AI Governance (UCRM) feature set. */
export const ICON_FEATURE_AI_GOVERNANCE = faRobot;

/** Represent the Control Automation feature set. */
export const ICON_FEATURE_CONTROL_AUTOMATION = faShieldHalved;

/** Represent global features. */
export const ICON_FEATURE_GLOBAL = faGlobe;

/** Represent the Reporting and Exports feature set. */
export const ICON_FEATURE_REPORTING_AND_EXPORTS = faChartLine;

/** Represent the Risk Management feature set. */
export const ICON_FEATURE_RISK_MANAGEMENT = faTableCells;

/** Represent the Third-Party Risk Management feature set. */
export const ICON_FEATURE_TPRM = faSitemap;

/** Represent a folder, directory, or other container of files or items. */
export const ICON_FOLDER = faFolder;

/** Represent a tooltip or help context. */
export const ICON_HELP = faQuestionCircle;

/** Indicate a "negative" scenario (rejecting something, something is missing, something is invalid, a boolean "false", etc.). This is similar to ICON_ARROW_DOWN, but is more appropriate for showing a point in time. Do not use this for deleting or closing, use ICON_DELETE_REMOVE or ICON_CLOSE instead. */
export const ICON_NEGATIVE = faTimes;

/** Indicate a "neutral" scenario (something in between positive and negative, such as partially complete). */
export const ICON_NEUTRAL = faMinusCircle;

/** Represent a notification. */
export const ICON_NOTIFICATION = faBell;

/** Indicate a "positive" scenario (confirming something, something is present, something is correct, a boolean "true", etc.). This is similar to ICON_ARROW_UP, but is more appropriate for showing a point in time. Do not use this for saving/submitting, use ICON_SAVE or ICON_SUBMIT instead. */
export const ICON_POSITIVE = faCheck;

/** Indicate something is prohibited, not allowed, deactivated, etc. */
export const ICON_PROHIBITED = faBan;

/** Represent security, privacy, or something related to authentication/authorization. */
export const ICON_SECURITY = faKey;

/** Sign in to an application or service. */
export const ICON_SIGN_IN = faSignInAlt;

/** Sign out of an application or service. */
export const ICON_SIGN_OUT = faSignOutAlt;

/** Represent an unknown situation. */
export const ICON_UNKNOWN = faQuestionCircle;

/** View something in a read-only mode. */
export const ICON_VIEW = faEye;

/** Indicate that something is hidden or can be hidden (e.g., a password). */
export const ICON_VIEW_HIDDEN = faEyeSlash;

/** Indicate a "waiting" state, such as when something is processing or loading but there is not a specific progress indicator. */
export const ICON_WAIT = faHourglassEnd;
